/**
 * JARVIS client state — one module-level store shared by the dock and the
 * full-page view, read through useJarvis() (useSyncExternalStore). No
 * provider to mount: render <JarvisDock /> anywhere inside the Router and the
 * /jarvis page sees the same conversation.
 */
import { useSyncExternalStore } from 'react';
import { apiFetch } from '../../utils/api';
import { speak, stopSpeaking, warmVoice, readSpeakerPref, writeSpeakerPref } from './voice';

const initial = {
  status: 'unknown',      // unknown | online | offline | forbidden | error
  model: null,
  conversations: [],
  currentId: null,
  items: [],              // { kind: 'user'|'assistant'|'tool'|'action'|'memory'|'error', ... }
  streaming: false,
  hud: null,              // current HUD line while streaming
  loadingConversation: false,
  speakerOn: readSpeakerPref(),
  speaking: false,
  listening: false,
  open: false,            // dock panel open
};

let state = initial;
const listeners = new Set();
let abortCtl = null;
const navigators = [];
let uid = 0;
const nextKey = () => `k${++uid}`;

function set(patch) {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  listeners.forEach((l) => l());
}
function subscribe(l) { listeners.add(l); return () => listeners.delete(l); }
const getSnapshot = () => state;

export function useJarvis() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** The most recently mounted component with a react-router navigate() handles navigate events. */
export function registerNavigator(fn) {
  navigators.push(fn);
  return () => { const i = navigators.lastIndexOf(fn); if (i >= 0) navigators.splice(i, 1); };
}

const withKeys = (items) => items.map((it) => ({ key: nextKey(), ...it }));

// ── API ─────────────────────────────────────────────────────────

export async function checkStatus() {
  try {
    const r = await apiFetch('/jarvis/status');
    set({ status: r.online ? 'online' : 'offline', model: r.model });
    if (r.online) refreshConversations();
  } catch (e) {
    set({ status: e.status === 403 ? 'forbidden' : 'error' });
  }
}

export async function refreshConversations() {
  try {
    const r = await apiFetch('/jarvis/conversations');
    set({ conversations: r.conversations || [] });
  } catch (e) {
    if (e.status === 503) set({ status: 'offline' });
  }
}

export async function openConversation(id) {
  if (state.streaming) return;
  set({ loadingConversation: true, currentId: id, items: [] });
  try {
    const r = await apiFetch(`/jarvis/conversations/${id}`);
    set({ items: withKeys(r.items || []), loadingConversation: false });
  } catch (e) {
    set({ loadingConversation: false, items: [{ key: nextKey(), kind: 'error', text: e.message }] });
  }
}

export function newConversation() {
  if (state.streaming) return;
  set({ currentId: null, items: [] });
}

export async function deleteConversation(id) {
  try {
    await apiFetch(`/jarvis/conversations/${id}`, { method: 'DELETE' });
    if (state.currentId === id) set({ currentId: null, items: [] });
    refreshConversations();
  } catch (e) { /* surfaced by the list staying put */ }
}

export function setOpen(open) { set({ open: !!open }); }
export function toggleOpen() { set((s) => ({ open: !s.open })); }
export function setListening(listening) {
  if (listening) stopSpeaking();
  set({ listening: !!listening, ...(listening ? { speaking: false } : {}) });
}
export function setSpeaker(on) {
  writeSpeakerPref(on);
  if (on) warmVoice(); else stopSpeaking();
  set({ speakerOn: !!on, ...(on ? {} : { speaking: false }) });
}

export function stopStreaming() {
  if (abortCtl) abortCtl.abort();
}

function updateItems(fn) { set((s) => ({ items: fn(s.items) })); }
function closeLive(items) {
  const last = items[items.length - 1];
  if (last && last.live) {
    if (!last.text.trim()) return items.slice(0, -1);
    return [...items.slice(0, -1), { ...last, live: false }];
  }
  return items;
}

/** Minimal SSE reader over a fetch body. Calls onEvent(name, data) per event. */
async function readSse(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = 'message';
      const data = [];
      for (const line of chunk.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).trim());
      }
      if (!data.length) continue; // comment / ping
      let parsed = null;
      try { parsed = JSON.parse(data.join('\n')); } catch (e) { parsed = null; }
      onEvent(event, parsed || {});
    }
  }
}

export async function sendMessage(rawText, { page } = {}) {
  const text = String(rawText || '').trim();
  if (!text || state.streaming) return;
  stopSpeaking();
  if (state.speakerOn) warmVoice(); // loads the neural voice while JARVIS thinks
  let id = state.currentId;
  set({ streaming: true, hud: 'Processing', speaking: false });
  updateItems((items) => [...items, { key: nextKey(), kind: 'user', text }, { key: nextKey(), kind: 'assistant', text: '', live: true }]);

  try {
    if (!id) {
      const r = await apiFetch('/jarvis/conversations', { method: 'POST' });
      id = r.conversation.id;
      set({ currentId: id });
    }
    abortCtl = new AbortController();
    const res = await fetch(`/api/jarvis/conversations/${id}/messages`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ text, page }),
      signal: abortCtl.signal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (res.status === 503) set({ status: 'offline' });
      throw new Error(body.error || `Request failed (${res.status})`);
    }

    let spoken = '';
    await readSse(res, (event, data) => {
      if (event === 'text') {
        spoken += data.delta || '';
        updateItems((items) => {
          const last = items[items.length - 1];
          if (last && last.live) return [...items.slice(0, -1), { ...last, text: last.text + (data.delta || '') }];
          return [...items, { key: nextKey(), kind: 'assistant', text: data.delta || '', live: true }];
        });
        set({ hud: 'Transmitting' });
      } else if (event === 'retry') {
        updateItems((items) => {
          const last = items[items.length - 1];
          return last && last.live ? [...items.slice(0, -1), { ...last, text: '' }] : items;
        });
      } else if (event === 'tool') {
        set({ hud: data.label || 'Working' });
        if (data.kind === 'read' || data.kind === 'ui') {
          updateItems((items) => [...closeLive(items), { key: nextKey(), kind: 'tool', name: data.name, label: data.label }]);
        }
      } else if (event === 'action_proposed') {
        spoken += ' ';
        updateItems((items) => [...closeLive(items), { key: nextKey(), kind: 'action', action: data }]);
      } else if (event === 'memory_saved') {
        updateItems((items) => [...closeLive(items), { key: nextKey(), kind: 'memory', memory: data, text: data.content }]);
      } else if (event === 'memory_forgotten') {
        updateItems((items) => [...closeLive(items), { key: nextKey(), kind: 'memory', forgotten: true, memory: data, text: data.content }]);
      } else if (event === 'navigate') {
        const nav = navigators[navigators.length - 1];
        if (nav && data.path) nav(data.path);
      } else if (event === 'status') {
        set({ hud: data.message });
      } else if (event === 'refusal' || event === 'error') {
        updateItems((items) => [...closeLive(items), { key: nextKey(), kind: 'error', text: data.message || 'Something went wrong.' }]);
      }
    });
    updateItems(closeLive);
    if (state.speakerOn && spoken.trim()) {
      set({ speaking: true });
      speak(spoken, () => set({ speaking: false }));
    }
  } catch (e) {
    if (e.name !== 'AbortError') {
      updateItems((items) => [...closeLive(items), { key: nextKey(), kind: 'error', text: e.message || 'JARVIS could not respond.' }]);
    } else {
      updateItems(closeLive);
    }
  } finally {
    abortCtl = null;
    set({ streaming: false, hud: null });
    refreshConversations();
  }
}

async function resolveAction(actionId, decision) {
  const patch = (fields) => updateItems((items) => items.map((it) => (it.kind === 'action' && it.action.id === actionId
    ? { ...it, action: { ...it.action, ...fields } } : it)));
  patch({ busy: true, error: null });
  try {
    const r = await apiFetch(`/jarvis/actions/${actionId}/${decision}`, { method: 'POST' });
    patch({ ...r.action, busy: false });
  } catch (e) {
    const status = e.code === 'EXPIRED' ? 'expired' : e.code === 'FAILED' ? 'failed' : undefined;
    patch({ busy: false, error: e.message, ...(status ? { status } : {}) });
  }
}
export const confirmAction = (id) => resolveAction(id, 'confirm');

/**
 * Undo a "Remembered" chip: a brand-new memory is deleted; one that refreshed
 * a near-identical memory is restored to its previous text.
 */
export async function undoMemory(itemKey) {
  const item = state.items.find((it) => it.key === itemKey && it.kind === 'memory');
  if (!item || !item.memory || item.undone || item.busy) return;
  const patch = (fields) => updateItems((items) => items.map((it) => (it.key === itemKey ? { ...it, ...fields } : it)));
  patch({ busy: true, error: null });
  const m = item.memory;
  try {
    if (m.previous && m.previous.content) {
      await apiFetch(`/jarvis/memories/${m.id}`, { method: 'PATCH', body: JSON.stringify({ content: m.previous.content, category: m.previous.category }) });
    } else {
      await apiFetch(`/jarvis/memories/${m.id}`, { method: 'DELETE' });
    }
    patch({ busy: false, undone: true });
  } catch (e) {
    patch({ busy: false, error: e.status === 404 ? 'Already removed.' : (e.message || 'Could not undo.'), ...(e.status === 404 ? { undone: true } : {}) });
  }
}
export const rejectAction = (id) => resolveAction(id, 'reject');
